import { Box, Card, CardContent, Typography, Chip, Divider } from '@mui/material'
import { FC } from 'react'
import { YearEndClose } from './types'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'

interface YearEndStatusCardProps {
  fiscalYear: number
  yearEndClose?: YearEndClose | null
  loading?: boolean
}

export const YearEndStatusCard: FC<YearEndStatusCardProps> = ({ fiscalYear, yearEndClose, loading = false }) => {
  const isClosed = !!yearEndClose
  const netIncome = yearEndClose?.netIncome ?? 0
  const isProfit = netIncome >= 0

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (date: string | Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <Card
        sx={{
          bgcolor: '#f5f5f5',
          borderLeft: '4px solid #9e9e9e',
          mb: 3
        }}
      >
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Loading year-end status...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      sx={{
        bgcolor: isClosed ? '#e8f5e9' : '#fff3e0',
        borderLeft: `4px solid ${isClosed ? '#4caf50' : '#ff9800'}`,
        mb: 3
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {isClosed ? <EventAvailableIcon color="success" /> : <EventBusyIcon color="warning" />}
              <Typography variant="h6" fontWeight="bold">
                Year-End Status: {fiscalYear}
              </Typography>
            </Box>

            <Chip
              label={isClosed ? 'YEAR CLOSED' : 'YEAR OPEN'}
              color={isClosed ? 'success' : 'warning'}
              size="small"
              sx={{ mb: 2, fontWeight: 'bold' }}
            />

            {isClosed && yearEndClose && (
              <>
                <Divider sx={{ my: 2 }} />

                <Box display="flex" gap={4} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Net Income
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {isProfit ? (
                        <TrendingUpIcon fontSize="small" color="success" />
                      ) : (
                        <TrendingDownIcon fontSize="small" color="error" />
                      )}
                      <Typography variant="h6" fontWeight="bold" color={isProfit ? 'success.main' : 'error.main'}>
                        {formatCurrency(netIncome)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Closed On
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(yearEndClose.closedAt)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Closed By
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {yearEndClose.closedBy.firstName} {yearEndClose.closedBy.lastName}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Closing Entry
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="primary">
                      #{yearEndClose?.closingJournalEntry?.journalEntryNumber || ' N/A'}
                    </Typography>
                  </Box>
                </Box>

                {yearEndClose.notes && (
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {yearEndClose.notes}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {!isClosed && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Year {fiscalYear} has not been closed yet. Close December to automatically trigger year-end close.
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
